import type { ContestDetail, ContestFixture } from '../../../api/contests';

export const contestFixturesById = (
  fixtures: readonly ContestFixture[],
): Map<string, ContestFixture> => new Map(fixtures.map((fixture) => [fixture.id, fixture]));

/** Tab badge + prizes heading: max of configured tiers vs awards (covers partial API payloads). */
export const contestDetailPrizesTabCount = (
  detail: Pick<ContestDetail, 'prizeTiers' | 'prizeAwards'>,
): number => Math.max(detail.prizeTiers?.length ?? 0, detail.prizeAwards?.length ?? 0);

export const settlementLabel = (settlement: {
  status: 'awaiting' | 'settled';
  result?: 'WIN' | 'LOSE' | 'VOID';
}): string => {
  if (settlement.status === 'awaiting') return 'Awaiting settlement';
  if (!settlement.result) return 'Settled';
  return `Settled: ${settlement.result}`;
};

export const prizeAwardColor = (
  status: ContestDetail['prizeAwards'][number]['status'],
): 'success' | 'error' | 'info' => {
  if (status === 'completed') return 'success';
  if (status === 'error') return 'error';
  return 'info';
};

export const formatFixtureStatusLabel = (status: ContestFixture['status']) =>
  status.charAt(0).toUpperCase() + status.slice(1);

/*
 * Lives in `contests/` now (CAE-1574): `reel-stats.ts` counts distinct markets
 * by the label the rows actually carry, so the two must read the same function.
 * Re-exported here so the detail tabs keep importing it from where they always
 * have.
 */
export { reelOptionMarketLabel } from '../../../contests/reel-option-market-label';

/*
 * `reelOptionFixtureLabel`, `reelOptionAttachmentChipLabel` and
 * `reelOptionAttachmentAriaLabel` went with CAE-1552.
 *
 * All three existed for `reel-option-attachment-chips.tsx`, the "fixture →
 * market" chip on the old reel panel's rows, and nothing else ever called them.
 * The rebuilt Edit › Reels row says the same thing as two plain spans with a dot
 * between them, and names its fixture through `resolveFixtureLabel` from
 * `@s2w/core` — which the chip's own version predated and quietly disagreed
 * with, since it read `homeTeam vs awayTeam` directly and so ignored both the
 * per-contest display-name override and tournament fixtures with no away team.
 */

export const fixtureStatusChipColor = (
  status: ContestFixture['status'],
): 'warning' | 'error' | 'success' | 'default' => {
  switch (status) {
    case 'scheduled':
      return 'warning';
    case 'live':
      return 'error';
    case 'finished':
      return 'default';
    case 'settled':
      return 'success';
    default:
      return 'default';
  }
};
